import { reactive } from 'vue'
import { storeToRefs } from 'pinia'
import Long from 'long'
import Jet, { typeDecimal } from '@/modules/Jet'
import { featureFlags as localFeatureFlags } from '@/modules/Onboarding/runtime/external/featureFlags'
import { useUserFlagsStore } from '@/store/userFlags'

// Numeric values matching analytics.js OnboardingStepTypeEnum
const STEP_TYPE = {
	QUESTION: 1,
	LOADER: 2,
	CONFIRM: 3,
	OTHER: 4,
}

// Numeric values matching analytics.js OnboardingPopupDoneEnum
const POPUP_CLOSE_TYPE = {
	CLOSE_BUTTON: 1,
	OFF_SCREEN_CLICK: 2,
	ANSWER_CLICK: 3,
	OTHER: 4,
}

const QUESTION_COMPONENT_TYPES = new Set([
	'listInput',
	'checkbox',
	'textInput',
	'dateInput',
	'numberInput',
	'imageInput',
	'rateInput',
	'voteInput',
	'tagsInput',
])

// Canonical button names for OnboardingButtonClick event
const BUTTON_NAME = {
	PRIVACY_POLICY: 'privacy_policy',
	TERMS_OF_USE: 'terms_of_use',
	LANGUAGE_SELECTOR: 'language_selector',
}

// Event names emitted by custom components via $emit('event-triggered', { name })
const CUSTOM_EVENT_NAME = {
	HEIGHT_FORM_ERROR: 'HeightFormError',
}

// href pattern → button_name for DOM-delegated OnboardingButtonClick tracking.
// Only links whose href matches one of these patterns trigger the event.
const LINK_HREF_TO_BUTTON = [
	{ pattern: /\/privacy/, name: BUTTON_NAME.PRIVACY_POLICY },
	{ pattern: /\/tos\b|\/terms/, name: BUTTON_NAME.TERMS_OF_USE },
]

// Screen-specific Jet event names (from PR #2, request_id=821).
// Events in this set are forwarded to Jet via jetScreenSpecificEvent().
const JET_SCREEN_EVENTS = new Set([
	'OnboardingWelcomeDone',
	'OnboardingAgeRangeSelectionDone',
	'OnboardingHeightSelectionDone',
	'OnboardingWeightSelectionDone',
	'OnboardingGoalWeightSelectionDone',
	'OnboardingBirthYearSelectionDone',
	'OnboardingGoalDateSelectionDone',
	'OnboardingEmailSubmitDone',
	'OnboardingHealthProblemRiskDone',
])

// Property-level type conversions for screen-specific events.
// Properties not listed here are passed as-is (strings, booleans).
const JET_PROPERTY_TYPE = {
	height: 'decimal',
	weight: 'decimal',
	year: 'long',
	secondsOnScreen: 'long',
	date: 'timestamp',
}

// Legacy CMS analyticsKey → Jet screen-specific event name + value property key.
// The renderer fires these via onHandleComponentEvents (onUnmounted) with { data: componentValue }.
// handleCustomAnalyticEvent in OnboardingView uses this map to forward data to jetScreenSpecificEvent.
const LEGACY_TO_JET_EVENT = {
	OnboardingHeightScreenDone: { jetName: 'OnboardingHeightSelectionDone', valueKey: 'height' },
	OnboardingWeightScreenDone: { jetName: 'OnboardingWeightSelectionDone', valueKey: 'weight' },
	OnboardingWeightGoalScreenDone: { jetName: 'OnboardingGoalWeightSelectionDone', valueKey: 'weight' },
	OnboardingHappyWeightScreenDone: { jetName: 'OnboardingGoalWeightSelectionDone', valueKey: 'weight' },
}

export {
	STEP_TYPE,
	BUTTON_NAME,
	POPUP_CLOSE_TYPE,
	JET_SCREEN_EVENTS,
	LEGACY_TO_JET_EVENT,
	CUSTOM_EVENT_NAME,
	jetGenerateStepId,
	jetComputeSecondsOnScreen,
}

// ─── Pure helpers (no state/store dependency) ────────────────────────────────

function formatTitle(name) {
	if (!name) return ''
	return name
		.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
		.replace(/[-\s]+/g, '_')
		.toUpperCase()
		.replace(/^ONBOARDING_/, '')
		.replace(/_SCREEN$/, '')
		.replace(/_DONE$/, '')
}

function jetIsConditional(screen) {
	const conditions = screen?.navigationParams?.conditionsForShow ?? []
	return conditions.some((c) => !(c.variableName === 'featureKey' && /^web_/.test(c.value)))
}

function jetComputeStepType(screen) {
	if (screen?.stepType != null) return screen.stepType

	const allComponents = [...(screen?.components ?? []), ...Object.values(screen?.bottomBlock ?? {})]
	const ids = allComponents.map((c) => c.id)

	if (ids.some((id) => QUESTION_COMPONENT_TYPES.has(id))) return STEP_TYPE.QUESTION
	if (ids.includes('button')) return STEP_TYPE.CONFIRM
	if (allComponents.some((c) => c.id === 'custom' && /loader/i.test(c.name))) return STEP_TYPE.LOADER

	return STEP_TYPE.OTHER
}

function jetGenerateStepId(title) {
	const ts = Date.now()
	const str = `${window.deviceId || ''}${title}${ts}`
	let hash = 5381
	for (let i = 0; i < str.length; i++) {
		hash = (((hash << 5) + hash) ^ str.charCodeAt(i)) >>> 0
	}
	return hash.toString(16) + ts.toString(36)
}

function jetComputeSecondsOnScreen(showTimestamp) {
	return showTimestamp ? Math.ceil((Date.now() - showTimestamp) / 1000) : null
}

function jetResolveButtonNameFromHref(href) {
	if (!href) return null
	for (const { pattern, name } of LINK_HREF_TO_BUTTON) {
		if (pattern.test(href)) return name
	}
	return null
}

// ─── Composable ──────────────────────────────────────────────────────────────

const JET_STEP_STATE_KEY = 'jet_last_step'

function persistStepState(stepId, title) {
	try {
		sessionStorage.setItem(JET_STEP_STATE_KEY, JSON.stringify({ stepId, title }))
	} catch {
		/* ignore */
	}
}

function readPersistedStepState() {
	try {
		return JSON.parse(sessionStorage.getItem(JET_STEP_STATE_KEY)) || null
	} catch {
		return null
	}
}

export function useOnboardingStepEvents() {
	const { getFlags: getUserFlags } = storeToRefs(useUserFlagsStore())

	const lastStep = readPersistedStepState()
	const stepEvents = reactive({
		currentStepId: lastStep?.stepId || null,
		previousStepId: null,
		currentTitle: lastStep?.title || '',
		previousTitle: '',
		showTimestamp: null,
		isBackwards: false,
		isConditional: false,
		isControl: true,
		isVariation: false,
		stepType: STEP_TYPE.OTHER,
		lastAnswerData: null,
		lastSecondsOnScreen: null,
		_popupStepId: null,
		_popupTitle: null,
	})

	// ─── Store-dependent helpers ─────────────────────────────────────────

	function jetIsAbsmartlyCondition(value) {
		if (value in localFeatureFlags) return false
		if (value === getUserFlags.value?.local?.funnelFeatureFlag) return false
		return true
	}

	function jetIsControl(screen) {
		const conditions = screen?.navigationParams?.conditionsForShow ?? []
		return !conditions.some((c) => c.variableName === 'featureKey' && jetIsAbsmartlyCondition(c.value))
	}

	// ─── Public API ─────────────────────────────────────────────────────

	function jetStepShow(screen, isBackwards, isVariation) {
		const title = formatTitle(screen?.name || '')
		const ts = Date.now()

		const isConditional = jetIsConditional(screen)
		const isControl = jetIsControl(screen)
		const stepType = jetComputeStepType(screen)
		const stepId = jetGenerateStepId(title)

		stepEvents.previousStepId = stepEvents.currentStepId
		stepEvents.previousTitle = stepEvents.currentTitle
		stepEvents.currentStepId = stepId
		stepEvents.currentTitle = title
		stepEvents.showTimestamp = ts
		persistStepState(stepId, title)
		stepEvents.isBackwards = isBackwards
		stepEvents.isConditional = isConditional
		stepEvents.isControl = isControl
		stepEvents.isVariation = !!isVariation
		stepEvents.stepType = stepType
		stepEvents.lastAnswerData = null

		Jet.logLogicalEvent('OnboardingStepShow', {
			onboardingStepId: stepId,
			...(stepEvents.previousStepId && { previousOnboardingStepId: stepEvents.previousStepId }),
			onboardingTitle: title,
			...(stepEvents.previousTitle && { previousOnboardingTitle: stepEvents.previousTitle }),
			isBackwards: isBackwards,
			isControl: isControl,
			isConditional: isConditional,
			isVariation: !!isVariation,
			stepType: stepType,
		})
	}

	function jetStepDone(isStepBack) {
		if (!stepEvents.showTimestamp) return

		const secondsOnScreen = jetComputeSecondsOnScreen(stepEvents.showTimestamp)

		stepEvents.lastSecondsOnScreen = secondsOnScreen

		// Per spec 8.15: when user leaves via back, data_* must be NULL
		const answer = isStepBack ? {} : stepEvents.lastAnswerData || {}

		Jet.logLogicalEvent('OnboardingStepDone', {
			onboardingStepId: stepEvents.currentStepId,
			...(stepEvents.previousStepId && { previousOnboardingStepId: stepEvents.previousStepId }),
			onboardingTitle: stepEvents.currentTitle,
			...(stepEvents.previousTitle && { previousOnboardingTitle: stepEvents.previousTitle }),
			isBackwards: stepEvents.isBackwards,
			isControl: stepEvents.isControl,
			isConditional: stepEvents.isConditional,
			isVariation: stepEvents.isVariation,
			stepType: stepEvents.stepType,
			...(secondsOnScreen !== null && { secondsOnScreen: Long.fromNumber(secondsOnScreen) }),
			isStepBack: isStepBack,
			...(answer.data_string != null && { dataString: answer.data_string }),
			...(answer.data_float != null && { dataFloat: typeDecimal(answer.data_float) }),
			...(answer.data_int != null && { dataInt: Long.fromNumber(answer.data_int) }),
			...(answer.data_array != null && { dataArray: answer.data_array }),
		})
	}

	function jetPopupShow(modal) {
		// Snapshot stepId/title — the renderer fires screen-changed (next screen)
		// before modal-closed, which would overwrite currentStepId.
		stepEvents._popupStepId = stepEvents.currentStepId
		stepEvents._popupTitle = stepEvents.currentTitle

		Jet.logLogicalEvent('OnboardingPopupShow', {
			...(stepEvents.currentStepId && { onboardingStepId: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboardingTitle: stepEvents.currentTitle }),
			...(modal?.name && { popupTitle: modal.name }),
		})
	}

	function jetPopupDone(modal, closeType = POPUP_CLOSE_TYPE.OTHER) {
		const stepId = stepEvents._popupStepId || stepEvents.currentStepId
		const title = stepEvents._popupTitle || stepEvents.currentTitle

		Jet.logLogicalEvent('OnboardingPopupDone', {
			...(stepId && { onboardingStepId: stepId }),
			...(title && { onboardingTitle: title }),
			...(modal?.name && { popupTitle: modal.name }),
			closeType,
		})

		stepEvents._popupStepId = null
		stepEvents._popupTitle = null
	}

	function jetButtonClick(buttonName) {
		Jet.logLogicalEvent('OnboardingButtonClick', {
			...(stepEvents.currentStepId && { onboardingStepId: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboardingTitle: stepEvents.currentTitle }),
			...(buttonName && { buttonName }),
		})
	}

	function jetHandleDelegatedClick(event) {
		const el = event.target.closest('a')
		if (!el) return

		const buttonName = jetResolveButtonNameFromHref(el.href)
		if (buttonName) {
			jetButtonClick(buttonName)
		}
	}

	function jetAlertDone(alertDescription, data) {
		Jet.logLogicalEvent('OnboardingAlertDone', {
			...(stepEvents.currentStepId && { onboardingStepId: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboardingTitle: stepEvents.currentTitle }),
			...(alertDescription && { alertDescription }),
			...(data != null && { data }),
		})
	}

	/**
	 * Forwards a renderer-emitted screen-specific event to Jet.
	 * Adds onboardingStepId automatically. onboardingTitle is NOT added —
	 * only a few events have it in their schema (e.g. OnboardingHealthProblemRiskDone).
	 * Callers must pass onboardingTitle explicitly in properties when needed.
	 * Unknown events (not in JET_SCREEN_EVENTS) are silently ignored.
	 */
	function jetScreenSpecificEvent(eventName, properties = {}) {
		if (!JET_SCREEN_EVENTS.has(eventName)) return

		const converted = {}
		if (stepEvents.currentStepId) {
			converted.onboardingStepId = stepEvents.currentStepId
		}

		for (const [key, value] of Object.entries(properties || {})) {
			if (key === 'data') continue // raw answer — already captured by jetCaptureAnswer
			if (value == null) continue

			const type = JET_PROPERTY_TYPE[key]
			if (type === 'decimal') {
				converted[key] = typeDecimal(value)
			} else if (type === 'long') {
				converted[key] = Long.fromNumber(value)
			} else if (type === 'timestamp') {
				converted[key] = new Date(value)
			} else {
				converted[key] = value
			}
		}

		// OnboardingWelcomeDone needs secondsOnScreen from the StepDone that just fired.
		// Timing: renderer emits @screen-changed (→ jetStepDone stores lastSecondsOnScreen)
		// before @event-triggered (→ jetScreenSpecificEvent uses it).
		if (eventName === 'OnboardingWelcomeDone' && !converted.secondsOnScreen && stepEvents.lastSecondsOnScreen != null) {
			converted.secondsOnScreen = Long.fromNumber(stepEvents.lastSecondsOnScreen)
		}

		Jet.logLogicalEvent(eventName, converted)
	}

	// Called from surveyEventHandler when a *ScreenNameDone* event fires
	function jetCaptureAnswer(data) {
		const raw = data?.properties?.data
		if (raw === undefined || raw === null) return

		// Compound data: {data_string: 'cm', data_float: 175}
		if (typeof raw === 'object' && !Array.isArray(raw)) {
			stepEvents.lastAnswerData = raw
			return
		}

		if (Array.isArray(raw)) {
			stepEvents.lastAnswerData = { data_array: [...raw].map(String).sort() }
		} else if (typeof raw === 'number') {
			if (Number.isInteger(raw)) {
				stepEvents.lastAnswerData = { data_int: raw }
			} else {
				stepEvents.lastAnswerData = { data_float: raw }
			}
		} else if (typeof raw === 'string') {
			stepEvents.lastAnswerData = { data_string: raw }
		}
	}

	return {
		stepEvents,
		jetStepShow,
		jetStepDone,
		jetPopupShow,
		jetPopupDone,
		jetButtonClick,
		jetHandleDelegatedClick,
		jetAlertDone,
		jetScreenSpecificEvent,
		jetCaptureAnswer,
	}
}
